import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@saucelabs/ds-button';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { IconManualFill } from '@saucelabs/ds-icons';
import {
  constants as userConstants,
  records as userRecords,
  selectors as userSelectors,
} from '@saucelabs/sl-module-user';

import { CONFIGURATION_DEVICE_TYPES, CONFIGURATION_TEST_TYPES } from '../constants';
import { handleTabsFormSubmit } from '../actions';
import { MANUAL_EVENTS, MANUAL_UI_ELEMENTS, trackEvent } from '../tracking';
import { selectors as testConfigurationSelectors } from '../ducks/testConfiguration';
import { selectors as rdsSelectors } from '../ducks/realDeviceSelection';
import { GlobalState } from '../reducers';

const trackEventTestLaunchedForConfig = (config) => {
  const {
    browserConfiguration,
    deviceType,
    mobileAppConfiguration,
    testType,
    tunnel,
    url,
    virtualDeviceConfiguration,
  } = config;
  const isAppTest = (deviceType === CONFIGURATION_DEVICE_TYPES.VIRTUAL_DEVICE) &&
    (testType === CONFIGURATION_TEST_TYPES.APP);

  trackEvent(MANUAL_EVENTS.TEST_LAUNCHED, {
    deviceType,
    isSampleApp: isAppTest ? mobileAppConfiguration?.isSampleApp() : false,
    sauceConnectProxy: tunnel,
    target: MANUAL_UI_ELEMENTS.RECENT_AND_SAVED_CONFIGS_START_BUTTON,
    testType: CONFIGURATION_TEST_TYPES[testType],
    url,
    ...((deviceType === CONFIGURATION_DEVICE_TYPES.DESKTOP_BROWSER) && {
      os: browserConfiguration?.osVersionDisplay,
      resolution: browserConfiguration?.res,
      vmBrowser: browserConfiguration?.browserDisplay,
      vmBrowserVersion: browserConfiguration?.browserVersionDisplay,
    }),
    ...((deviceType === CONFIGURATION_DEVICE_TYPES.VIRTUAL_DEVICE) && {
      device: virtualDeviceConfiguration?.device,
      os: virtualDeviceConfiguration?.version,
    }),
  });
};

const StartVirtualSessionButton: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const submitDisabled = useSelector<GlobalState, boolean>(
    testConfigurationSelectors.isVirtualSubmitDisabledSelector,
  );
  const testConfiguration = useSelector(testConfigurationSelectors.testConfigurationSelector);
  const isVerifiedUser = useSelector(userSelectors.isVerifiedUserSelector);
  const email = useSelector(userSelectors.getUserEmail);
  const isManualMinutesExpired = useSelector(rdsSelectors.isManualMinutesExpiredSelector);
  const isCreditsExpired = useSelector((state: any) => (
    state.usage?.credits?.total_available_credits <= 0 &&
    state.user.get('user_type') === 'freemium'
  ));

  const appSelectionRequired = testConfiguration.testType === CONFIGURATION_TEST_TYPES.APP &&
    !testConfiguration.mobileAppConfiguration;
  const showTooltip = !isVerifiedUser ||
    isManualMinutesExpired ||
    isCreditsExpired ||
    appSelectionRequired;

  const getToolTipText = () => {
    if (isManualMinutesExpired) {
      return 'To keep testing, upgrade now for unlimited minutes.';
    }
    if (!isVerifiedUser && email) {
      return `To test, verify the email we sent to ${email}`;
    }
    if (isCreditsExpired) {
      return 'To keep testing, upgrade now for more credits.';
    }
    if (appSelectionRequired) {
      return 'Select an application before starting the test.';
    }
    return 'To test, verify your email.';
  };

  const startTestButton = (
    <div className="relative mt-1.5 ">
      <Button
        className="w-full mt-0.5 justify-center"
        data-qa="start-virtual-session-button"
        disabled={submitDisabled || isManualMinutesExpired || isCreditsExpired}
        onPress={() => {
          trackEventTestLaunchedForConfig(testConfiguration);
          void dispatch(handleTabsFormSubmit());
        }}
        prefixIcon={<IconManualFill />}
      >
        Start Test
      </Button>
    </div>
  );

  return (
    <>
      {showTooltip && (
        <Tooltip
          data-qa="start-virtual-session-button-tooltip"
          delay={300}
          placement="top"
          title={getToolTipText()}
        >
          {startTestButton}
        </Tooltip>
      )}
      {!showTooltip && startTestButton}
    </>
  );
};

export default StartVirtualSessionButton;
